import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isApiError } from '@/lib/apiError';

import { configuracionApi } from '../api';
import type {
  ContactoLaboratorioUpdate,
  ContadorProtocoloResponse,
  ContadorProtocoloUpdate,
  FirmanteUpdate,
} from '../types';

const QUERY_KEY_BASE = ['configuracion'] as const;

export const configuracionKeys = {
  all: QUERY_KEY_BASE,
  firmante: () => [...QUERY_KEY_BASE, 'firmante'] as const,
  contactoLaboratorio: () => [...QUERY_KEY_BASE, 'contacto-laboratorio'] as const,
  contadorProtocolo: [...QUERY_KEY_BASE, 'contador-protocolo'] as const,
};

/**
 * Si el firmante no está configurado, el backend devuelve 404.
 * El componente debe manejar isError + isNotFound como "no configurado".
 */
export function useFirmante() {
  return useQuery({
    queryKey: configuracionKeys.firmante(),
    queryFn: ({ signal }) => configuracionApi.getFirmante(signal),
    retry: (failureCount, error) => {
      if (isApiError(error) && error.isNotFound()) return false;
      return failureCount < 1;
    },
  });
}

export function useUpdateFirmante() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FirmanteUpdate) => configuracionApi.updateFirmante(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(configuracionKeys.firmante(), updated);
    },
  });
}

export function useContactoLaboratorio() {
  return useQuery({
    queryKey: configuracionKeys.contactoLaboratorio(),
    queryFn: ({ signal }) => configuracionApi.getContactoLaboratorio(signal),
    retry: (failureCount, error) => {
      if (isApiError(error) && error.isNotFound()) return false;
      return failureCount < 1;
    },
  });
}

export function useUpdateContactoLaboratorio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ContactoLaboratorioUpdate) =>
      configuracionApi.updateContactoLaboratorio(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(configuracionKeys.contactoLaboratorio(), updated);
    },
  });
}

export function useContadorProtocolo() {
  return useQuery({
    queryKey: configuracionKeys.contadorProtocolo,
    queryFn: ({ signal }) => configuracionApi.getContadorProtocolo(signal),
  });
}

export function useUpdateContadorProtocolo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ContadorProtocoloUpdate) =>
      configuracionApi.updateContadorProtocolo(data),
    onSuccess: (updated: ContadorProtocoloResponse) => {
      queryClient.setQueryData(configuracionKeys.contadorProtocolo, updated);
    },
  });
}
